import { useUpdateWearableExpiryDate } from 'api/Customers'
import { ConnectedField as BaseConnectedField } from 'components/ConnectedField'
import { Form, Formik } from 'formik'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button } from 'ui/components/Button'
import { FormikDatePicker } from 'ui/components/Form/DatePicker'
import { Input } from 'ui/components/Form/Input'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import { Modal, ModalButtons } from 'ui/Modal'

const ConnectedField = BaseConnectedField as any

export const SetExpiryDateModal = ({ isOpen, onClose, barcode }) => {
  const { t } = useTranslation()
  const { clientId } = useParams<{ clientId: string }>()
  const notification = useNotificationContext()

  const { mutate: updateExpiryDate, isLoading: isUpdatingExpiryDate } =
    useUpdateWearableExpiryDate()

  const getInitialValues = () => {
    if (barcode?.expires_at) {
      const expiryDateTime = DateTime.fromJSDate(barcode.expires_at)

      return {
        expires_at_date: expiryDateTime.toJSDate(),
        expires_at_time: expiryDateTime.toFormat('HH:mm'),
      }
    }

    return {
      expires_at_date: new Date(),
      expires_at_time: '23:59',
    }
  }

  const handleSubmit = (values) => {
    if (!values.expires_at_date || !barcode) return

    const dateTime = DateTime.fromJSDate(values.expires_at_date)
      .set({
        hour: parseInt(values.expires_at_time.split(':')[0]),
        minute: parseInt(values.expires_at_time.split(':')[1]),
        second: 0,
      })
      .toJSDate()

    updateExpiryDate(
      {
        customerId: clientId,
        barcodeId: barcode.id,
        updateWearableExpiryDate: {
          expires_at: dateTime,
        },
      },
      {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t('clients.client.profile.set_expiry_date_modal.notification.title')}
              description={t(
                'clients.client.profile.set_expiry_date_modal.notification.description'
              )}
              variant="success"
              autoDismiss
            />
          )
          onClose()
        },
        onError: (response) => {
          notification.notifyApiError(response)
        },
      }
    )
  }

  return (
    <Modal
      title={t('clients.client_profile.set_expiry_date_modal.title')}
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
    >
      <Formik initialValues={getInitialValues()} onSubmit={handleSubmit} enableReinitialize>
        {({ values }) => (
          <Form>
            <div className="space-y-4">
              <ConnectedField
                name="expires_at_date"
                minDate={Date.now()}
                label={t('clients.client_profile.set_expiry_date_modal.expiry_date')}
                component={FormikDatePicker}
              />

              <ConnectedField
                name="expires_at_time"
                label={t('clients.client_profile.set_expiry_date_modal.expiry_time')}
                component={Input}
                type="time"
              />
            </div>

            <ModalButtons>
              <Button
                label={t('button.save')}
                variant="primary"
                type="submit"
                disabled={!values.expires_at_date}
                loading={isUpdatingExpiryDate}
              />
              <Button label={t('modal_button.cancel')} onClick={onClose} />
            </ModalButtons>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
